/**
 * ManageVPE Operations Hook
 * Handles all business logic and operations for the ManageVPE component
 * Extracted from ManageVPE.tsx for better separation of concerns
 */

import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useServiceWithAuth } from '@/hooks/useServiceWithAuth';
import { VPEManagementService, type CreateVPEData, type UpdateVPEData } from '@/services/features/VPEManagementService';
import { ManageVPEState, ManageVPEStateActions } from './useManageESState';

export interface ManageVPEOperations {
  // Core Operations
  fetchVPEs: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  handleDelete: () => Promise<void>;
  handleLogout: () => void;
  
  // UI Operations
  handleAddClick: () => void;
  handleEditClick: (vpe: any) => void;
  handleDeleteClick: (vpe: any) => void;
  handleTaskAssignedChange: (checked: boolean | "indeterminate") => void;
  
  // Pagination Operations
  handlePageChange: (page: number) => void;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  handleRowsPerPageChange: (rows: number) => void;
  
  // Validation
  validateForm: () => boolean;
}

export const useManageESOperations = (
  state: ManageVPEState,
  actions: ManageVPEStateActions,
  computed: { totalPages: number }
) => {
  const { hasuraToken, user, token, logout } = useAuth();
  const { toast } = useToast();

  // Get service instance with proper auth context
  const vpeManagementService = useServiceWithAuth(VPEManagementService);

  // Fetch VPEs on component mount and when hasuraToken changes
  useEffect(() => {
    if (hasuraToken) {
      fetchVPEs();
    }
  }, [hasuraToken]);

  // Ensure current page is valid when data or pagination changes
  useEffect(() => {
    if (state.currentPage > computed.totalPages && computed.totalPages > 0) {
      actions.setCurrentPage(1);
    }
  }, [state.currentPage, computed.totalPages]);

  /**
   * Fetch all VPEs from the service
   */
  const fetchVPEs = async () => {
    if (!hasuraToken) {
      console.warn('useManageVPEOperations: No hasura token available');
      return;
    }

    console.log('useManageVPEOperations: Starting to fetch VPEs');
    actions.setIsLoading(true);
    try {
      const vpes = await vpeManagementService.fetchVPEs();
      console.log('useManageVPEOperations: Successfully fetched VPEs:', vpes.length);
      actions.setVpes(vpes);
    } catch (error) {
      console.error('useManageVPEOperations: Error fetching VPEs:', error);
      toast({
        title: "Fetch Failed",
        description: "Failed to fetch VPEs. Please try again.",
        variant: "destructive"
      });
    } finally {
      actions.setIsLoading(false);
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    // For edit mode, check if any changes were made
    if (state.isEditMode && state.selectedVPE) {
      const hasChanges = vpeManagementService.hasChanges(state.selectedVPE, {
        first_name: state.firstName.trim(),
        last_name: state.lastName.trim(),
        email: state.email.trim(),
        task_assigned: state.taskAssigned ? "deferred" : null,
        password: state.password.trim()
      });

      if (!hasChanges) {
        toast({
          title: "Validation Error",
          description: "Please add details and update",
          variant: "destructive"
        });
        return false;
      }
    }

    // Validate using service validation
    const validation = vpeManagementService.validateVPEData({
      first_name: state.firstName,
      last_name: state.lastName,
      email: state.email,
      password: state.password
    }, state.isEditMode);

    if (!validation.isValid) {
      toast({
        title: "Validation Error",
        description: validation.errors[0],
        variant: "destructive"
      });
      return false;
    }

    // Password confirmation check
    if (!state.isEditMode || state.password) {
      if (state.password !== state.confirmPassword) {
        toast({
          title: "Validation Error",
          description: "Passwords do not match.",
          variant: "destructive"
        });
        return false;
      }
    }

    return true;
  };

  /**
   * Handle form submission (create or update VPE)
   */
  const handleSubmit = async () => {
    if (!user?.id) {
      toast({
        title: "Authentication Error",
        description: "User authentication required.",
        variant: "destructive"
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    actions.setIsCreateLoading(true);
    
    try {
      // Add 1 second delay to match original behavior
      await new Promise(resolve => setTimeout(resolve, 1000));

      const vpeData = {
        first_name: state.firstName,
        last_name: state.lastName,
        email: state.email,
        task_assigned: state.taskAssigned ? "deferred" : null,
      };

      if (state.isEditMode) {
        // Update existing VPE
        const updateData: UpdateVPEData = {
          ...vpeData,
          ...(state.password && { password: state.password })
        };
        
        await vpeManagementService.updateVPE(state.selectedVPE!.hbox_user_id, updateData);
        
        toast({
          title: "Success",
          description: "ES Updated successfully",
        });
      } else {
        // Create new VPE
        const createData: CreateVPEData = {
          ...vpeData,
          password: state.password
        };
        
        await vpeManagementService.createVPE(createData);
        
        toast({
          title: "Success",
          description: "ES Created successfully",
        });
      }

      actions.setShowDialog(false);
      actions.resetForm();
      await fetchVPEs();
    } catch (error) {
      console.error(`Error ${state.isEditMode ? 'updating' : 'creating'} VPE:`, error);
      toast({
        title: `${state.isEditMode ? 'Update' : 'Creation'} Failed`,
        description: error instanceof Error ? error.message : `Failed to ${state.isEditMode ? 'update' : 'create'} VPE. Please try again.`,
        variant: "destructive"
      });
    } finally {
      actions.setIsCreateLoading(false);
    }
  };

  /**
   * Handle VPE deletion
   */
  const handleDelete = async () => {
    if (!state.selectedVPE || !user?.id) return;

    actions.setIsDeleteLoading(true);
    try {
      await vpeManagementService.deleteVPE(state.selectedVPE.hbox_user_id);
      
      toast({
        title: "Success",
        description: "ES Deleted successfully",
      });
      
      actions.setShowDeleteDialog(false);
      actions.setSelectedVPE(null);
      await fetchVPEs();
    } catch (error) {
      console.error('Error deleting VPE:', error);
      toast({
        title: "Delete Failed",
        description: error instanceof Error ? error.message : "Failed to delete VPE. Please try again.",
        variant: "destructive"
      });
    } finally {
      actions.setIsDeleteLoading(false);
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    actions.setShowLogoutDialog(false);
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
  };

  /**
   * Handle add VPE click
   */
  const handleAddClick = () => {
    actions.resetForm();
    actions.setShowDialog(true);
  };

  /**
   * Handle edit VPE click
   */
  const handleEditClick = (vpe: any) => {
    actions.populateFormForEdit(vpe);
    actions.setShowDialog(true);
  };

  /**
   * Handle delete VPE click
   */
  const handleDeleteClick = (vpe: any) => {
    actions.setDeleteButtonLoading(vpe.hbox_user_id);
    actions.setSelectedVPE(vpe);
    actions.setShowDeleteDialog(true);
    // Reset loading after dialog opens
    setTimeout(() => actions.setDeleteButtonLoading(null), 100);
  };

  /**
   * Handle task assigned checkbox change
   */
  const handleTaskAssignedChange = (checked: boolean | "indeterminate") => {
    actions.setTaskAssigned(checked === true);
  };

  /**
   * Handle pagination - page change
   */
  const handlePageChange = (page: number) => {
    actions.setCurrentPage(page);
  };

  /**
   * Handle pagination - previous page
   */
  const handlePreviousPage = () => {
    actions.setCurrentPage(Math.max(state.currentPage - 1, 1));
  };

  /**
   * Handle pagination - next page
   */
  const handleNextPage = () => {
    actions.setCurrentPage(Math.min(state.currentPage + 1, computed.totalPages));
  };

  /**
   * Handle rows per page change
   */
  const handleRowsPerPageChange = (rows: number) => {
    actions.setRowsPerPage(rows);
    actions.setCurrentPage(1); // Reset to first page when changing rows per page
  };

  return {
    fetchVPEs,
    handleSubmit,
    handleDelete,
    handleLogout,
    handleAddClick,
    handleEditClick,
    handleDeleteClick,
    handleTaskAssignedChange,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    handleRowsPerPageChange,
    validateForm,
  };
};